/**
 * Product Card Controller
 * Handles image swatch switching, quick add to cart, inline option drawer,
 * and client-side wishlist persistence. Built with native ES6 for Shopify OS 2.0.
 */

document.addEventListener('DOMContentLoaded', () => {
  initProductCardListeners();
  syncWishlistButtons();
});

function initProductCardListeners() {
  // Delegate all product card interactions to document
  document.addEventListener('click', (event) => {
    // 1. Direct Quick Add Button
    const quickAddBtn = event.target.closest('[data-quick-add-btn]');
    if (quickAddBtn) {
      event.preventDefault();
      const variantId = quickAddBtn.dataset.variantId;
      if (variantId) {
        handleQuickAdd(quickAddBtn, variantId);
      }
      return;
    }

    // 2. Toggle Quick Options Drawer
    const optionsToggle = event.target.closest('[data-quick-options-toggle]');
    if (optionsToggle) {
      event.preventDefault();
      const card = optionsToggle.closest('[data-product-card]');
      if (!card) return;

      const optionsDrawer = card.querySelector('.product-card__quick-options');
      if (optionsDrawer) {
        const isHidden = optionsDrawer.hidden;
        // Close any other open option drawers on the page
        document.querySelectorAll('.product-card__quick-options').forEach((drawer) => {
          drawer.hidden = true;
          const toggle = drawer.closest('[data-product-card]')?.querySelector('[data-quick-options-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        });

        optionsDrawer.hidden = !isHidden;
        optionsToggle.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
      }
      return;
    }

    // 3. Close Quick Options Drawer
    const closeBtn = event.target.closest('[data-quick-options-close]');
    if (closeBtn) {
      event.preventDefault();
      const optionsDrawer = closeBtn.closest('.product-card__quick-options');
      if (optionsDrawer) {
        optionsDrawer.hidden = true;
        const toggle = optionsDrawer.closest('[data-product-card]')?.querySelector('[data-quick-options-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    // 4. Quick Add from Options List
    const variantBtn = event.target.closest('[data-quick-add-variant]');
    if (variantBtn && !variantBtn.disabled) {
      event.preventDefault();
      const variantId = variantBtn.dataset.variantId;
      const card = variantBtn.closest('[data-product-card]');
      const optionsDrawer = variantBtn.closest('.product-card__quick-options');

      if (variantId) {
        // Set loading on the variant button
        variantBtn.disabled = true;
        variantBtn.style.opacity = '0.6';

        handleQuickAdd(variantBtn, variantId, () => {
          variantBtn.disabled = false;
          variantBtn.style.opacity = '';
          if (optionsDrawer) optionsDrawer.hidden = true;
          const toggle = card?.querySelector('[data-quick-options-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        });
      }
      return;
    }

    // 5. Variant Swatch Click
    const swatch = event.target.closest('[data-swatch-val]');
    if (swatch) {
      event.preventDefault();
      handleSwatchChange(swatch);
      return;
    }

    // 6. Wishlist Button Click
    const wishlistBtn = event.target.closest('[data-wishlist-btn]');
    if (wishlistBtn) {
      event.preventDefault();
      handleWishlistToggle(wishlistBtn);
      return;
    }

    // Click outside to close open quick option drawers
    if (!event.target.closest('.product-card__quick-options') && !event.target.closest('[data-quick-options-toggle]')) {
      document.querySelectorAll('.product-card__quick-options:not([hidden])').forEach((drawer) => {
        drawer.hidden = true;
        const toggle = drawer.closest('[data-product-card]')?.querySelector('[data-quick-options-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    }
  });

  // Keyboard navigation for swatches and options
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const openDrawers = document.querySelectorAll('.product-card__quick-options:not([hidden])');
      openDrawers.forEach((drawer) => {
        drawer.hidden = true;
        const toggle = drawer.closest('[data-product-card]')?.querySelector('[data-quick-options-toggle]');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
          toggle.focus();
        }
      });
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      const swatch = event.target.closest('[data-swatch-val]');
      if (swatch) {
        event.preventDefault();
        handleSwatchChange(swatch);
      }
    }
  });
}

/**
 * Handle adding a variant to cart via Shopify's standard /cart/add.js
 */
async function handleQuickAdd(button, variantId, callback) {
  if (button.classList.contains('is-loading')) return;

  const originalContent = button.innerHTML;
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');

  try {
    const endpoint = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js';

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || data.message || 'Could not add to cart');
    }

    // Success State
    button.classList.remove('is-loading');
    button.classList.add('is-success');
    const textSpan = button.querySelector('.product-card__btn-text') || button;
    if (textSpan) {
      textSpan.textContent = '✓ Added';
    }

    // Fetch updated cart or update count
    fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart.js` : '/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        if (typeof window.updateHeaderCartCount === 'function') {
          window.updateHeaderCartCount(cart.item_count);
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, item_count: cart.item_count } }));
      })
      .catch(() => {});

    setTimeout(() => {
      button.classList.remove('is-success');
      button.setAttribute('aria-busy', 'false');
      button.innerHTML = originalContent;
      if (typeof callback === 'function') callback();
    }, 1800);
  } catch (error) {
    button.classList.remove('is-loading');
    button.setAttribute('aria-busy', 'false');
    button.innerHTML = originalContent;
    alert(error.message || 'Failed to add item to cart. Please try again.');
    if (typeof callback === 'function') callback();
  }
}

/**
 * Handle Color Swatch Selection
 */
function handleSwatchChange(swatch) {
  const card = swatch.closest('[data-product-card]');
  if (!card) return;

  // Toggle active class & ARIA
  const group = swatch.closest('.product-card__swatches');
  if (group) {
    group.querySelectorAll('.product-card__swatch').forEach((s) => {
      s.classList.remove('product-card__swatch--active');
      s.setAttribute('aria-checked', 'false');
    });
  }
  swatch.classList.add('product-card__swatch--active');
  swatch.setAttribute('aria-checked', 'true');

  // Update primary card image if variant image exists
  const primaryImg = card.querySelector('.product-card__image--primary');
  if (primaryImg && swatch.dataset.imageSrc) {
    primaryImg.src = swatch.dataset.imageSrc;
    if (swatch.dataset.imageSrcset) {
      primaryImg.srcset = swatch.dataset.imageSrcset;
    }
  }

  // Update quick add button target variant
  const quickAddBtn = card.querySelector('[data-quick-add-btn]');
  if (quickAddBtn && swatch.dataset.variantId) {
    quickAddBtn.dataset.variantId = swatch.dataset.variantId;
  }

  // Update price display if variant price differs
  if (swatch.dataset.price) {
    const regularPrice = card.querySelector('.price--regular');
    if (regularPrice) {
      regularPrice.textContent = swatch.dataset.price;
    }
    const comparePrice = card.querySelector('.price--compare');
    if (comparePrice) {
      if (swatch.dataset.compareAt) {
        comparePrice.textContent = swatch.dataset.compareAt;
        comparePrice.hidden = false;
      } else {
        comparePrice.hidden = true;
      }
    }
  }
}

/**
 * Honest Client-Side Wishlist Handling (localStorage)
 */
const WISHLIST_STORAGE_KEY = 'theme_product_wishlist';

function getStoredWishlist() {
  try {
    const stored = localStorage.getItem(WISHLIST_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveWishlist(items) {
  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
  } catch {}
}

function syncWishlistButtons() {
  const list = getStoredWishlist();
  if (!list.length) return;

  document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
    const handle = btn.dataset.productHandle;
    if (handle && list.includes(handle)) {
      btn.classList.add('product-card__wishlist--active');
      btn.setAttribute('aria-pressed', 'true');
    }
  });
}

function handleWishlistToggle(btn) {
  const handle = btn.dataset.productHandle;
  if (!handle) return;

  let list = getStoredWishlist();
  const isPresent = list.includes(handle);

  if (isPresent) {
    list = list.filter((item) => item !== handle);
    btn.classList.remove('product-card__wishlist--active');
    btn.setAttribute('aria-pressed', 'false');
  } else {
    list.push(handle);
    btn.classList.add('product-card__wishlist--active');
    btn.setAttribute('aria-pressed', 'true');
  }

  saveWishlist(list);
  document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: list, handle, added: !isPresent } }));
}
